'use client';

import type React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  AndroidIcon,
  AppleIcon,
  QR_PLATFORMS,
  type QrDownloadPlatform,
} from '../lib/qr-download-data';

interface QrDownloadDialogProperties {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  initialPlatform?: QrDownloadPlatform;
  footerNote?: string;
  children?: React.ReactNode;
}

export const QrDownloadDialog: React.FC<QrDownloadDialogProperties> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  initialPlatform = 'ios',
  footerNote,
  children,
}) => {
  const [activeTab, setActiveTab] = useState<QrDownloadPlatform>(initialPlatform);
  const [isRendered, setIsRendered] = useState(false);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialPlatform);
      setIsRendered(true);
      const frame = requestAnimationFrame(() => {
        setAnimate(true);
      });
      return () => {
        cancelAnimationFrame(frame);
      };
    }

    setAnimate(false);
    const timer = setTimeout(() => {
      setIsRendered(false);
    }, 300);
    return () => {
      clearTimeout(timer);
    };
  }, [isOpen, initialPlatform]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    globalThis.addEventListener('keydown', handleEsc);
    return () => {
      globalThis.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isRendered) return null;

  const platform = QR_PLATFORMS[activeTab];
  const icons: Record<QrDownloadPlatform, React.ReactNode> = {
    ios: <AppleIcon />,
    android: <AndroidIcon />,
  };

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${animate ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
        role="presentation"
      />
      <div
        className={`bg-card/80 border-border/40 fixed right-0 bottom-0 left-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t shadow-2xl backdrop-blur-xl transition-all duration-300 lg:top-1/2 lg:right-auto lg:bottom-auto lg:left-1/2 lg:max-h-[90vh] lg:max-w-md lg:-translate-x-1/2 lg:-translate-y-1/2 lg:rounded-2xl lg:border ${
          animate ? 'translate-y-0' : 'pointer-events-none translate-y-full'
        } ${animate ? 'lg:scale-100 lg:opacity-100' : 'lg:pointer-events-none lg:scale-95 lg:opacity-0'}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="border-border flex items-center justify-between border-b px-6 py-4">
          <div className="flex flex-col">
            <h3 className="text-foreground text-base font-bold">{title}</h3>
            {subtitle ? (
              <span className="text-muted-foreground mt-0.5 text-[11px]">{subtitle}</span>
            ) : null}
          </div>
          <button
            className="bg-muted hover:bg-accent/10 text-muted-foreground hover:text-foreground flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 text-xl font-medium transition-colors"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            &times;
          </button>
        </div>

        <div className="bg-muted/50 border-border/50 relative mx-6 mt-5 flex rounded-xl border p-1 backdrop-blur-md">
          {(Object.keys(QR_PLATFORMS) as QrDownloadPlatform[]).map((platformKey) => (
            <button
              key={platformKey}
              onClick={() => {
                setActiveTab(platformKey);
              }}
              className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg border-none py-2 text-xs font-semibold transition-all duration-200 ${
                activeTab === platformKey
                  ? 'bg-card/80 text-[var(--accent)] shadow-sm backdrop-blur-md'
                  : 'text-muted-foreground hover:text-foreground bg-transparent'
              }`}
              type="button"
            >
              <span className="flex h-4 w-4 items-center justify-center">{icons[platformKey]}</span>
              <span>{QR_PLATFORMS[platformKey].label}</span>
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-6 p-6">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="border-border group qr-box-animated relative overflow-hidden rounded-2xl border bg-white p-3 shadow-md transition-all duration-300 select-none hover:shadow-lg">
              <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-tr from-[var(--accent)] to-orange-400 opacity-0 transition-opacity duration-300 group-hover:opacity-[0.03]" />

              <div className="qr-scanner-overlay">
                <div className="qr-scan-line" />
              </div>

              <div className="qr-corner top-3 left-3 rounded-tl border-t-2 border-l-2" />
              <div className="qr-corner top-3 right-3 rounded-tr border-t-2 border-r-2" />
              <div className="qr-corner bottom-3 left-3 rounded-bl border-b-2 border-l-2" />
              <div className="qr-corner right-3 bottom-3 rounded-br border-r-2 border-b-2" />

              <img
                src={platform.qrImage}
                alt={`QR code for ${platform.store}`}
                className="relative z-10 h-40 w-40 rounded-lg object-contain"
              />
            </div>

            <div className="flex flex-col gap-1">
              <p className="text-foreground text-xs font-semibold">Scan with your phone camera</p>
              <p className="text-muted-foreground max-w-[280px] text-[11px]">
                Point your camera at the QR code to open the official {platform.store} listing.
              </p>
            </div>

            <a
              href={platform.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-[var(--accent)] py-2.5 text-xs font-semibold text-white no-underline shadow-sm transition-all hover:bg-[#d04200] hover:text-white hover:opacity-95 active:scale-[0.98]"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                {icons[activeTab]}
              </span>
              Open {platform.store}
            </a>
          </div>

          {children}
        </div>

        {footerNote ? (
          <div className="border-border/40 bg-muted/20 border-t px-6 py-3 backdrop-blur-md">
            <p className="text-muted-foreground text-center text-[10px]">{footerNote}</p>
          </div>
        ) : null}
      </div>
    </>,
    document.body,
  );
};
